/** @file Rewrites mixDefaults sides after an animation rename; counts colliding pairs dropped. */
import type { Draft } from 'immer';
import type { Doc } from '@pose2d/format/types';
import { mixNames } from '@pose2d/format/doc/mixNames';
import { names } from '@pose2d/format/names';

/** Call after the rename: rewrites the sides that `from` left dangling; returns the number of colliding pairs dropped. */
export function renameMixSides(draft: Draft<Doc>, from: string, to: string): number {
  const pairs = draft.mixDefaults;
  if (!pairs || from === to) return 0;
  // A side that still names an animation or @DIR base stays (the same rule as renameFixBases). A dangling side equal
  // to `from` becomes `to`; one equal to `from`'s base becomes `to`'s base. A rewritten pair landing on an existing
  // pair is dropped and counted.
  const known = mixNames(draft.animations);
  const oldBase = names.splitAnimation(from).base;
  const newBase = names.splitAnimation(to).base;
  const side = (s: string): string => {
    if (known.has(s)) return s;
    if (s === from) return to;
    return s === oldBase ? newBase : s;
  };
  const kept: Record<string, number> = {};
  const moved: [string, number][] = [];
  for (const [pair, seconds] of Object.entries(pairs)) {
    const [a = '', b = ''] = pair.split('>');
    const next = `${side(a)}>${side(b)}`;
    if (next === pair) kept[pair] = seconds;
    else moved.push([next, seconds]);
  }
  if (moved.length === 0) return 0;
  let dropped = 0;
  for (const [pair, seconds] of moved) {
    if (Object.hasOwn(kept, pair)) dropped += 1;
    else kept[pair] = seconds;
  }
  draft.mixDefaults = kept;
  return dropped;
}
